import { Injectable } from '@angular/core';
import { AcmicService } from '@mapfre-design-library/src/public_api';
import { WidgetContent } from '@models/widget/widget-content.interface';
import { WidgetFactoryService } from './widget-factory.service';
import { ActionType } from '@models/widget/action-type.enum';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { CompanyService } from '@services/my-insurance/company.service';
import{ NavigationService } from '@services/navigation.service';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class ActionWidgetService {
  policyList: any[];
  customerWidgets: WidgetContent[];
  isAcmic: boolean;
  company: string;

  private widgetsSubject = new BehaviorSubject<WidgetContent[]>([]);
  customerWidgets$ = this.widgetsSubject.asObservable();

  constructor(
    private acmicService: AcmicService,
    private factory: WidgetFactoryService,
    private http: HttpClient,
    private companyService: CompanyService,
    private navService: NavigationService
  ) {
    this.isAcmic = this.acmicService.isACMIC();
     this.companyService.$currentCompany.subscribe((res)=>{
      this.company = res;
    })
   }

   public setCustomerWidgets(widgets: WidgetContent[]){
    this.customerWidgets = widgets;
    this.widgetsSubject.next(widgets);
   }

   public convertAndSet(widgetsInJson: string){
    const widgetsToUpdate: WidgetContent[] = [];

    const jsonObj: { id, customPosition }[] = JSON.parse(widgetsInJson);
    jsonObj.forEach( obj => {
      const widgetType: ActionType = obj.id as ActionType; 
       if (
        widgetType === ActionType.AddDocuments &&
        this.isDocumentUploadExcluded()
      ) {
        return;
      }
      const widget = this.factory.createWidgetContent(widgetType);
      widget.customPosition = obj.customPosition;
      widgetsToUpdate.push(widget);
    });

    this.setCustomerWidgets(widgetsToUpdate);
   }

 
  public getDefaultWidgets(): WidgetContent[] {

    const defaultWidget = [
      this.factory.createWidgetContent(ActionType.AccessMyPolicy),
      this.factory.createWidgetContent(ActionType.MakePayment),
      this.factory.createWidgetContent(ActionType.ReportClaim),
    ];

    if (this.isAcmic ||this.companyService.hasGWPolicy) {
      defaultWidget.push(
        this.factory.createWidgetContent(ActionType.AddDocuments)
      );
    } else {
      defaultWidget.push(
        this.factory.createWidgetContent(ActionType.ViewTransactions)
      );
    }

    defaultWidget.sort((a, b) => a.position - b.position);
    return defaultWidget;
  }

   public getAllWidgets(): WidgetContent[]{
    const allWidgets: WidgetContent[] = [
      this.factory.createWidgetContent(ActionType.AccessMyPolicy),
      this.factory.createWidgetContent(ActionType.MakePayment),
      this.factory.createWidgetContent(ActionType.ReportClaim),
      this.factory.createWidgetContent(ActionType.ManageAutopay),
      this.factory.createWidgetContent(ActionType.SeeLastInvoice),
      this.factory.createWidgetContent(ActionType.ViewTransactions),
      this.factory.createWidgetContent(ActionType.AskQuestion),
      this.factory.createWidgetContent(ActionType.FindRoadsideAid),
    ];

    if(!this.isAcmic){
      if (!this.isDocumentUploadExcluded()) {
        allWidgets.push(this.factory.createWidgetContent(ActionType.AddDocuments));
      }
      allWidgets.push(this.factory.createWidgetContent(ActionType.ExploreFAQs));
      allWidgets.push(this.factory.createWidgetContent(ActionType.DiscoverRewards));
       allWidgets.push(this.factory.createWidgetContent(ActionType.BillingRequest));
    } else {
      if (this.isAcmic || !this.isDocumentUploadExcluded()) {
        allWidgets.push(
          this.factory.createWidgetContent(ActionType.AddDocuments)
        );
      }

      allWidgets.push(
        this.factory.createWidgetContent(ActionType.GetIdCards)
      );}

    allWidgets.sort((a, b) => a.position - b.position);
    return allWidgets;
   }
    private isDocumentUploadExcluded(): boolean {
          return !this.companyService.hasGWPolicy;
        }

   public saveCustomActionWidgets(customizedWidgets: WidgetContent[]){
    return this.save(this.getPayload(customizedWidgets)).subscribe(
      (res) => {
        if(res){
          this.setCustomerWidgets(customizedWidgets);
        }
      }
    );
   }

  public saveLastActivitiesAsWidgets(){    
    this.navService.viewActionHistory();
    const actions = this.navService.getActionHistory();
    if((this.customerWidgets && this.customerWidgets.length) || actions.length === 0)
      return;

    const widgetsToSave: WidgetContent[] = [null, null, null, null];
    for(let i = 0; i < widgetsToSave.length; i++){
      if(i == actions.length)
        break;

      widgetsToSave[i] = this.factory.createWidgetContent(actions[i])
    }

    const payload = this.getPayload(widgetsToSave);
    this.saveOnLogout(payload);
  }

   clearCustomWidgets(){
    this.widgetsSubject.next([]);
   }

   private getPayload(widgets: WidgetContent[]): string {
     let defaultWidgets = (this.customerWidgets && this.customerWidgets.length === 4) 
                            ? [...this.customerWidgets] 
                            : this.getAllWidgets();
    const idsToRemove = new Set(widgets.filter(widget => widget).map(widget => widget.id));
    defaultWidgets = defaultWidgets.filter(widget => !idsToRemove.has(widget.id));

    let pos = 0;
    for(let i = 0; i < widgets.length; i++){
      if(widgets[i] === null){
        widgets[i] = defaultWidgets[pos++];
      }
      widgets[i].customPosition = i;
    }

    const selectedPropertiesData = widgets.map(({ id, customPosition }) => ({ id, customPosition }));
    return JSON.stringify(selectedPropertiesData);
  }

  private save(payload: string): Observable<any> {
    const user = JSON.parse(localStorage.getItem('currentUser'));

    const url = `${environment.backend_server_url_account}/custom-action-widgets/${user.username.toLowerCase()}`;
    // const url = `/b2c-account/v1/custom-action-widgets/${user.username.toLowerCase()}`;
    return this.http.post(url, payload);
  }

  private saveOnLogout(payload: string) {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const url = `${environment.backend_server_url_account}/custom-action-widgets/${currentUser.username.toLowerCase()}`;

    fetch(url, {
      method: 'POST',
      body: payload,
      keepalive: true,
      headers:  {
        Authorization: `Bearer ${currentUser.access_token.access_token}`,
        id: currentUser.id.id,
        name: currentUser.username,
        company: this.isAcmic ? 'AAA' : 'MAPFRE'
      }
    });
  }
}
